import React, {
  createContext, useContext, useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { setActiveMarinaIdContext } from '../lib/api';
import type { AdminRole } from '@harbormaster/shared';

// ── Types ────────────────────────────────────────────────────

export interface AdminProfile {
  id: string;
  organizationId: number;
  marinaId: number | null;
  role: AdminRole;
  name: string;
  email: string;
  accessibleMarinaIds: number[];
}

/**
 * Captures *why* the admin profile failed to load so the UI can show
 * something more useful than a generic "no access" page.
 *
 * - kind: 'forbidden'  → backend reached, returned 403 (truly no admin profile)
 * - kind: 'network'    → fetch threw / could not reach backend at all
 * - kind: 'server'     → backend reached, returned 5xx or other unexpected status
 * - kind: 'parse'      → backend returned non-JSON (often a misrouted SPA HTML page)
 */
export type ProfileLoadError =
  | { kind: 'forbidden'; status: 403; code: string; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'server'; status: number; code: string; message: string }
  | { kind: 'parse'; status: number; message: string };

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: AdminProfile | null;
  /** True when GET /admin/me returned 403 (no admin profile or incomplete access). */
  adminAccessDenied: boolean;
  /** Last failure reason for /admin/me (cleared on success). */
  profileError: ProfileLoadError | null;
  loading: boolean;
  // Active marina for org_admin switching
  activeMarinaId: number | null;
  setActiveMarina: (id: number | null) => void;
  // Convenience booleans
  isOrgAdmin: boolean;
  isSuperAdmin: boolean;
  canWrite: boolean;
  // Auth actions
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
  /** Re-runs GET /admin/me with the current session token. Used by the "Try again" button. */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const ACTIVE_MARINA_KEY = 'vh_active_marina_id';

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** 250ms, 1000ms — keeps total worst-case under ~3.5s including a slow last fetch. */
function backoffMs(attempt: number): number {
  return attempt === 1 ? 250 : 1000;
}

// ── Provider ─────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [adminAccessDenied, setAdminAccessDenied] = useState(false);
  const [profileError, setProfileError] = useState<ProfileLoadError | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeMarinaId, setActiveMarinaIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(ACTIVE_MARINA_KEY);
    return stored ? parseInt(stored, 10) : null;
  });

  const setActiveMarina = useCallback((id: number | null) => {
    setActiveMarinaIdState(id);
    setActiveMarinaIdContext(id);
    if (id === null) {
      localStorage.removeItem(ACTIVE_MARINA_KEY);
    } else {
      localStorage.setItem(ACTIVE_MARINA_KEY, String(id));
    }
  }, []);

  // Keep API module in sync with React state (incl. localStorage rehydrate) so ?marinaId= is correct on first request.
  setActiveMarinaIdContext(activeMarinaId);

  // Load admin profile from the backend (not Supabase directly)
  // We call /api/v1/admin/me to get the full resolved profile.
  //
  // Robustness:
  // - Retries up to 3x for network/5xx/parse errors with exponential backoff
  //   so a transient hiccup (Railway cold start, brief connectivity blip,
  //   browser extension delay) doesn't kick the user to the "no access" page.
  // - 403 is *not* retried — that's a definitive signal from the backend.
  // - Tracks the last failure reason in `profileError` so the UI can show
  //   something specific (status code + retry button) instead of a dead end.
  const loadProfile = useCallback(async (accessToken: string) => {
    const apiBase = import.meta.env.VITE_API_URL ?? '';
    const url = `${apiBase}/api/v1/admin/me`;
    const maxAttempts = 3;
    let lastError: ProfileLoadError | null = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (res.status === 403) {
          let code = 'NOT_ADMIN';
          let message = 'Forbidden';
          try {
            const body = await res.json() as { error?: string; code?: string };
            if (body.code) code = body.code;
            if (body.error) message = body.error;
          } catch { /* keep defaults */ }
          setProfile(null);
          setAdminAccessDenied(true);
          setProfileError({ kind: 'forbidden', status: 403, code, message });
          return;
        }

        if (!res.ok) {
          let code = `HTTP_${res.status}`;
          let message = `Request failed (${res.status})`;
          try {
            const body = await res.json() as { error?: string; code?: string };
            if (body.code) code = body.code;
            if (body.error) message = body.error;
          } catch { /* keep defaults */ }
          lastError = { kind: 'server', status: res.status, code, message };
          // 5xx and other non-403 errors are worth retrying
          if (attempt < maxAttempts) {
            await sleep(backoffMs(attempt));
            continue;
          }
          setProfile(null);
          setAdminAccessDenied(false);
          setProfileError(lastError);
          return;
        }

        // 2xx: try to parse. If parse fails (e.g. SPA HTML returned because
        // VITE_API_URL was empty in build), treat as a parse error and retry —
        // there's nothing useful in repeating, but we surface it clearly.
        let data: {
          id: string;
          organization_id: number;
          marina_id: number | null;
          role: AdminRole;
          name: string;
          email: string;
          accessible_marina_ids: number[];
        };
        try {
          data = await res.json();
        } catch {
          lastError = {
            kind: 'parse',
            status: res.status,
            message: 'Backend returned a non-JSON response (likely a routing/config issue).',
          };
          setProfile(null);
          setAdminAccessDenied(false);
          setProfileError(lastError);
          return;
        }

        // Success
        setAdminAccessDenied(false);
        setProfileError(null);
        const p: AdminProfile = {
          id: data.id,
          organizationId: data.organization_id,
          marinaId: data.marina_id,
          role: data.role,
          name: data.name,
          email: data.email,
          accessibleMarinaIds: data.accessible_marina_ids,
        };
        setProfile(p);

        if (p.role === 'org_admin' || p.role === 'platform_superadmin') {
          const storedId = activeMarinaId;
          if (storedId !== null && p.accessibleMarinaIds.length > 0 && !p.accessibleMarinaIds.includes(storedId)) {
            setActiveMarina(null);
          }
          if (storedId === null && p.accessibleMarinaIds.length === 1) {
            setActiveMarina(p.accessibleMarinaIds[0]);
          }
        } else if (p.marinaId !== null) {
          setActiveMarina(p.marinaId);
        }
        return;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Network request failed';
        lastError = { kind: 'network', message };
        if (attempt < maxAttempts) {
          await sleep(backoffMs(attempt));
          continue;
        }
        setProfile(null);
        setAdminAccessDenied(false);
        setProfileError(lastError);
        return;
      }
    }
  }, [activeMarinaId, setActiveMarina]);

  const loadProfileRef = useRef(loadProfile);
  loadProfileRef.current = loadProfile;

  useEffect(() => {
    const runLoadProfile = (token: string) => loadProfileRef.current(token);

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.access_token) {
        runLoadProfile(s.access_token).finally(() => setLoading(false));
      } else {
        setProfile(null);
        setAdminAccessDenied(false);
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.access_token) {
        runLoadProfile(s.access_token).finally(() => setLoading(false));
      } else {
        setProfile(null);
        setAdminAccessDenied(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setAdminAccessDenied(false);
    setProfileError(null);
    setActiveMarina(null);
  }, [setActiveMarina]);

  const refreshProfile = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return;
    setLoading(true);
    try {
      await loadProfileRef.current(token);
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });
    return { error: error?.message ?? null };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error?.message ?? null };
  }, []);

  const isOrgAdmin = profile?.role === 'org_admin' || profile?.role === 'platform_superadmin';
  const isSuperAdmin = profile?.role === 'platform_superadmin';
  // Explicitly false while loading — avoids false-positive when profile === null
  const canWrite = profile !== null && profile.role !== 'marina_staff';

  const value = useMemo(
    () => ({
      user, session, profile, adminAccessDenied, profileError, loading,
      activeMarinaId, setActiveMarina,
      isOrgAdmin, isSuperAdmin, canWrite,
      signIn, signOut, resetPassword, updatePassword, refreshProfile,
    }),
    [user, session, profile, adminAccessDenied, profileError, loading, activeMarinaId, setActiveMarina,
      isOrgAdmin, isSuperAdmin, canWrite, signIn, signOut, resetPassword, updatePassword, refreshProfile],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
